import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { EventListenerManager } from "abweb/utils/EventRegistration";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { ClipboardUtils } from "abweb/utils/clipboard/ClipboardUtils";
/**
 * Custom clipboard object that allows you to support copy/paste in your applications
 * and interact with the system clipboard.
 */
export class AbClipboard {
  constructor() {
    _defineProperty(this, "eventListeners", void 0);
    _defineProperty(this, "eventsRegistered", false);
    _defineProperty(this, "copyInProgress", false);
    _defineProperty(this, "dataTransferFromCopy", null);
    _defineProperty(this, "dataTransferFromPaste", null);
    this.eventListeners = new EventListenerManager();
    this.initializeMaps();
  }
  initializeMaps() {
    this.copyExactMap = new Map();
    this.copyMap = new Map();
    this.cutExactMap = new Map();
    this.cutMap = new Map();
    this.pasteExactMap = new Map();
    this.pasteMap = new Map();
    this.clipboardData = new Map();
  }
  registerEvents(d) {
    if (this.eventsRegistered) {
      return;
    }

    // TODO : what if each call ends with different document ?
    // add assertion ?
    // another level of mapping ?
    // ignore for now ?
    this.doc = d;
    this.eventListeners.registerEventListener(this.doc, "copy", this.copyHandler.bind(this));
    this.eventListeners.registerEventListener(this.doc, "cut", this.cutHandler.bind(this));
    this.eventListeners.registerEventListener(this.doc, "paste", this.pasteHandler.bind(this));
    this.eventsRegistered = true;
  }

  /**
   * Register to receive notifications for any copy event coming out of the target element.
   * @param el Target element that has focus.
   * @param includeDescendants If true, any descendant element of el that has focus will also trigger the callback
   * @param callback Callback function when the copy event is handled.
   */
  registerForCopy(el, includeDescendants, callback) {
    if (includeDescendants) {
      this.copyMap.set(el, callback);
    } else {
      this.copyExactMap.set(el, callback);
    }
    this.registerEvents(el.ownerDocument);
  }

  /**
   * Register to receive notifications for any cut event coming out of the target element.
   * @param el Target element that has focus.
   * @param includeDescendants If true, any descendant element of el that has focus will also trigger the callback
   * @param callback Callback function when the cut event is handled.
   */
  registerForCut(el, includeDescendants, callback) {
    if (includeDescendants) {
      this.cutMap.set(el, callback);
    } else {
      this.cutExactMap.set(el, callback);
    }
    this.registerEvents(el.ownerDocument);
  }

  /**
   * Unregisters for copy notifications previously registered in registerForCopy().
   * @param el Target element.
   */
  unregisterForCopy(el) {
    this.copyMap.delete(el);
    this.copyExactMap.delete(el);
  }

  /**
   * Unregisters for cut notifications previously registered in registerForCut().
   * @param el Target element.
   */
  unregisterForCut(el) {
    this.cutMap.delete(el);
    this.cutExactMap.delete(el);
  }

  /**
   * Register to receive notifications for any paste event coming out of the target element.
   * @param el Target element that has focus.
   * @param includeDescendants If true, any descendant element of el that has focus will also trigger the callback
   * @param callback Callback function when the paste event is handled.
   */
  registerForPaste(el, includeDescendants, callback) {
    if (includeDescendants) {
      this.pasteMap.set(el, callback);
    } else {
      this.pasteExactMap.set(el, callback);
    }
    this.registerEvents(el.ownerDocument);
  }

  /**
   * Unregisters for paste notifications previously registered in registerForPaste().
   * @param el Target element.
   */
  unregisterForPaste(el) {
    this.pasteMap.delete(el);
    this.pasteExactMap.delete(el);
  }

  /**
   * Puts data into the clipboard.
   * @param format Desired format. If format = "text", this method will attempt to place the data string
   * on the system clipboard. For this format, it will only succeed when called as part of a copy event handler or
   * when doCopy() is called from an event handler. For other formats, the data will go into this clipboard object
   * and is not subject to any restrictions.
   * @param data Data to go into the clipboard.
   * If format = "text", "text/plain" or "text/html", the data must be a string.
   * If data is NOT a string, we use only our internal clipboard format since the system clipboard
   * can only accept text as a payload.
   */
  setData(format, data) {
    if (typeof data !== "string") {
      this.clipboardData.set(format, data);
      return;
    }
    if (this.dataTransferFromCopy != null) {
      // We are in a copy event handler so we should be able to use the DataTransfer object
      try {
        this.dataTransferFromCopy.setData(format, data);
        return;
      } catch (err) {
        AbClipboard.LOG.warn(`setData dataTransferFromCopy.setData(${format}) failed: ${err.message}`);
      }
    }
    if (format.indexOf("text") !== 0) {
      // Assume this is a custom format but we can't access the DataTransfer object
      // so just use private clipboard and hope for the best
      AbClipboard.LOG.info(`setData dataTransferFromCopy.setData(${format}) Using private clipboard since DataTransfer not available`);
      this.clipboardData.set(format, data);
      return;
    }

    // cheesy/sleazy hack to put text into system clipboard
    this.copyInProgress = true; // set to avoid reentrance through copyHandler()
    const textArea = this.doc.createElement("textarea");

    // TODO: add styling if needed and position it absolutely outside visible viewport
    textArea.value = data.toString();
    this.doc.body.appendChild(textArea);
    textArea.select();
    try {
      const ret = this.doc.execCommand("copy");
      if (!ret) {
        AbClipboard.LOG.warn(`setData document.execCommand("copy") failed`);
      }
    } catch (err) {
      AbClipboard.LOG.warn(`setData document.execCommand("copy") failed ${err.message}`);
    }
    this.doc.body.removeChild(textArea);
    this.copyInProgress = false;
  }

  /**
   * Gets data from the clipboard.
   * @param format Desired format.
   * If format = "text" and you wish to get data from the system clipboard,
   * this method will only succeed when called as part of a paste event handler or when doPaste() is called from an event
   * handler. For non-text formats, this call is not subject to any restrictions.
   * @returns Data on clipboard for desired format, or undefined if not found.
   */
  getData(format) {
    const data = this.clipboardData.get(format);
    if (data) {
      // We found our format in our private clipboard
      return data;
    }

    // See if we can get it from system clipboard
    let dt = this.dataTransferFromPaste;
    if (dt == null) {
      // We are not in a paste event callback, so we can ask window to see if we can access it
      // but only IE will let us do this
      const w = window;
      dt = w.clipboardData;
    }
    if (dt == null) {
      // No luck so just return private data
      AbClipboard.LOG.info(`getData(${format}) cannot access DataTransfer object`);
      return data;
    }
    try {
      return dt.getData(format);
    } catch (err) {
      AbClipboard.LOG.warn(`getData dataTransfer.getData(${format}) failed ${err.message}`);
      return data;
    }
  }

  /**
   * Clears all data from the private clipboard.
   */
  clear() {
    if (this.dataTransferFromCopy != null) {
      try {
        this.dataTransferFromCopy.clearData();
      } catch (err) {
        AbClipboard.LOG.warn(`clear dataTransferFromCopy.clearData() failed ${err.message}`);
      }
    }
    this.clipboardData.clear();
  }

  /**
   * Copies data as text into system clipboard.
   * Note this calls setData("text") so see the restrictions on calling that method.
   * @param cells string cell data as a string or an Array of rectangular data for Excel
   * @param format Desired format. If format = "text", this method will attempt to place the data string
   * on the system clipboard. For this format, it will only succeed when called as part of a copy event handler or
   * when doCopy() is called from an event handler. For other formats, the data will go into this clipboard object
   * and is not subject to any restrictions.
   */
  copyCellsToSystemClipboardForExcel(cells, format = "text") {
    const s = ClipboardUtils.encodeCellsForExcel(cells);
    this.setData(format, s);
  }

  /**
   * Destroys this clipboard instance.
   * This method clears all private clipboard content, and unregisters copy/cut/paste event handlers.
   * You should always call this method from your component's destroy() method to avoid memory leaks.
   */
  destroy() {
    this.eventListeners.unregister();
    this.eventsRegistered = false;
    this.initializeMaps();
  }
  copyHandler(event) {
    if (this.copyInProgress) {
      // Ignore this
      return;
    }
    const clipboardEvent = event;
    this.dataTransferFromCopy = clipboardEvent.clipboardData;
    try {
      const el = this.doc.activeElement;
      if (el != null) {
        if (this.performCallbacks(el, this.copyExactMap, this.copyMap)) {
          event.preventDefault();
        }
        el.focus();
      }
    } finally {
      this.dataTransferFromCopy = null;
    }
  }
  cutHandler(event) {
    if (this.copyInProgress) {
      // Ignore this
      return;
    }
    const clipboardEvent = event;
    this.dataTransferFromCopy = clipboardEvent.clipboardData;
    try {
      const el = this.doc.activeElement;
      if (el != null) {
        if (this.performCallbacks(el, this.cutExactMap, this.cutMap)) {
          event.preventDefault();
        }
        el.focus();
      }
    } finally {
      this.dataTransferFromCopy = null;
    }
  }

  /**
   * Handler for paste event on document.
   * @param event
   */
  pasteHandler(event) {
    const clipboardEvent = event;
    this.dataTransferFromPaste = clipboardEvent.clipboardData;
    try {
      if (this.performCallbacks(this.doc.activeElement, this.pasteExactMap, this.pasteMap)) {
        event.preventDefault();
      }
    } finally {
      this.dataTransferFromPaste = null;
    }
  }
  performCallbacks(el, exactMap, map) {
    // Check for exact match
    let callback = exactMap.get(el);
    if (callback != null) {
      let res = callback(el, this);
      if (res !== false) {
        return true;
      }
    }

    // Walk up ancestor elements looking for a match in map
    let walker = el;
    while (true) {
      callback = map.get(walker);
      if (callback != null) {
        let res = callback(el, this);
        if (res !== false) {
          return true;
        }
      }
      if (walker === this.doc.body || walker.parentElement === null) {
        // We are done
        return false;
      }
      walker = walker.parentElement;
    }
  }
}
_defineProperty(AbClipboard, "LOG", LogUtils.getLogger(__filename));
//# sourceMappingURL=AbClipboard.js.map
